/* Shared display/playback owner for snippets/ambient-video.liquid only.
   Shopify video_tag supplies native sources. Each component activates on actual
   visibility, keeps its preview through failures, and reveals a real frame.
   One-shot videos prepare a paused first frame shortly before viewport entry.
   Reconnection preserves ready/ended state; source changes reset it.
   Background media stays control-free; failures retain the preview/background.
   No global media selection, pauseAllMedia, download queue or Swiper changes. */
(() => {
  if (customElements.get('ambient-video')) return;
  const stillPreloadMargin = 400;

  class AmbientVideo extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.epoch = (this.epoch || 0) + 1;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 991px)');
      this.preview = this.querySelector('.ambient-video__preview');
      this.listen(this.preview, 'error', () => { this.preview.hidden = true; });
      if (this.preview?.complete && !this.preview.naturalWidth) this.preview.hidden = true;
      this.listen(this.motion, 'change', () => this.updateVisibility());
      this.listen(this.mobile, 'change', () => this.updateVisibility());
      this.listen(document, 'visibilitychange', () => this.updateVisibility());
      this.listen(window, 'pageshow', () => this.updateVisibility());
      // A failed autoplay can be retried by a real gesture while in view.
      this.listen(document, 'pointerdown', () => {
        if (this.blocked) this.updateVisibility();
      }, { passive: true });
      const existing = this.querySelector('.ambient-video__video');
      if (existing) this.bindVideo(existing);
      if ('IntersectionObserver' in window) {
        const thresholds = [0, 0.01, this.threshold(false), this.threshold(true)];
        this.observer = new IntersectionObserver(() => this.updateVisibility(), {
          threshold: [...new Set(thresholds)].sort((a, b) => a - b),
        });
        this.observer.observe(this);
        if (this.dataset.playback === 'once' && !this.video) {
          // Keep real-viewport playback thresholds independent of preload distance.
          this.preloadObserver = new IntersectionObserver(() => this.updateVisibility(), {
            rootMargin: `${stillPreloadMargin}px 0px`,
          });
          this.preloadObserver.observe(this);
        }
      } else {
        this.listen(window, 'scroll', () => this.updateVisibility(), { passive: true });
        this.listen(window, 'resize', () => this.updateVisibility(), { passive: true });
      }
      this.updateVisibility();
    }

    disconnectedCallback() {
      this.epoch += 1;
      this.cancelFrame();
      this.observer?.disconnect();
      this.preloadObserver?.disconnect();
      this.lifecycle?.abort();
      this.lifecycle = null;
      this.pendingPlay = null;
      this.video?.pause();
    }

    listen(target, event, callback, options = {}) {
      target?.addEventListener(event, callback, { ...options, signal: this.lifecycle.signal });
    }

    threshold(mobile = this.mobile.matches) {
      const value = Number(mobile ? this.dataset.thresholdMobile : this.dataset.threshold);
      return Number.isFinite(value) && value > 0 ? Math.min(value, 1) : 0.01;
    }

    visibleRatio() {
      const rect = this.getBoundingClientRect();
      if (!rect.height || !rect.width || rect.right <= 0 || rect.left >= innerWidth) return 0;
      return Math.max(0, Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0)) / rect.height;
    }

    nearViewport() {
      const rect = this.getBoundingClientRect();
      return rect.height > 0 && rect.width > 0 && rect.right > 0 && rect.left < innerWidth &&
        rect.bottom >= -stillPreloadMargin && rect.top <= innerHeight + stillPreloadMargin;
    }

    mountVideo() {
      if (this.video) return;
      const template = this.querySelector('[data-ambient-video-template]');
      const video = template?.content.querySelector('video')?.cloneNode(true);
      if (!video) return;
      // One responsive preview path; remove Shopify's native mini-poster before
      // insertion, while preserving its generated MP4/HLS source selection.
      video.removeAttribute('poster');
      video.querySelectorAll('img').forEach((image) => image.remove());
      video.muted = true;
      video.playsInline = true;
      // One-shot sections show a decoded frame without calling play() early.
      if (this.dataset.playback === 'once') video.preload = 'auto';
      if (this.dataset.label) video.setAttribute('aria-label', this.dataset.label);
      this.bindVideo(video);
      this.prepend(video);
      this.preloadObserver?.disconnect();
    }

    bindVideo(video) {
      this.video = video;
      this.failedSources = new Set();
      this.listen(video, 'playing', () => {
        this.blocked = false;
        this.watchFrame();
      });
      this.listen(video, 'timeupdate', () => this.checkFallbackFrame());
      this.listen(video, 'loadeddata', () => this.watchFrame());
      this.listen(video, 'ended', () => {
        this.finished = this.dataset.playback === 'once';
      });
      this.listen(video, 'emptied', () => {
        this.epoch += 1;
        this.cancelFrame();
        this.pendingPlay = null;
        this.finished = false;
        this.failed = false;
        this.failedSources.clear();
        this.removeAttribute('data-ready');
        this.updateVisibility();
      });
      this.listen(video, 'error', () => this.mediaFailed());
      const sources = [...video.querySelectorAll('source')];
      const supported = sources.filter((source) => !source.type || video.canPlayType(source.type));
      sources.forEach((source) => this.listen(source, 'error', () => {
        this.failedSources.add(source);
        const candidates = supported.length ? supported : sources;
        if (candidates.every((candidate) => this.failedSources.has(candidate))) this.mediaFailed();
      }));
      if (video.error) this.mediaFailed();
      if (video.readyState >= 2) this.watchFrame();
    }

    mediaFailed() {
      this.failed = true;
      this.epoch += 1;
      this.pendingPlay = null;
      this.cancelFrame();
      this.removeAttribute('data-ready');
      this.video?.pause();
    }

    motionAllowed() {
      return !this.motion.matches;
    }

    mayPlay() {
      return this.isConnected && !!this.lifecycle && !document.hidden &&
        this.visibleRatio() >= this.threshold() && this.motionAllowed() &&
        !this.finished && !this.failed;
    }

    updateVisibility() {
      if (!this.isConnected || !this.lifecycle) return;
      // Prepare nearby stills before entry. Reduced motion may load
      // this still frame, but mayPlay() continues to prevent animation.
      if (this.dataset.playback === 'once' && !document.hidden && !this.failed && this.nearViewport()) {
        this.mountVideo();
        this.watchFrame();
      }
      if (this.mayPlay()) {
        this.mountVideo();
        this.play();
      } else if (this.video && !this.video.paused) {
        this.video.pause();
      }
    }

    play() {
      const video = this.video;
      if (!video || this.pendingPlay || !video.paused || !this.mayPlay()) return;
      const epoch = this.epoch;
      this.watchFrame();
      // Call in the gesture's own task (Safari), then record actual success.
      let result;
      try {
        result = video.play();
      } catch (error) {
        this.playFailed(error);
        return;
      }
      const attempt = Promise.resolve(result);
      this.pendingPlay = attempt;
      attempt.then(() => {
        if (!this.lifecycle || epoch !== this.epoch) return;
        this.blocked = false;
        if (!this.mayPlay()) video.pause();
        this.watchFrame();
      }).catch((error) => {
        if (this.lifecycle && epoch === this.epoch) this.playFailed(error);
      }).finally(() => {
        if (this.pendingPlay === attempt) this.pendingPlay = null;
      });
    }

    playFailed(error) {
      if (error?.name === 'NotSupportedError') this.mediaFailed();
      else if (error?.name !== 'AbortError') this.blocked = true;
    }

    watchFrame() {
      if (!this.video || this.hasAttribute('data-ready') || this.failed) return;
      // A paused initial still already has decoded image data at HAVE_CURRENT_DATA.
      // It needs no playback progress or future frame callback (including reconnect).
      if (this.dataset.playback === 'once' && this.video.paused && this.video.currentTime === 0 && this.video.readyState >= 2) {
        this.cancelFrame();
        this.reveal();
        return;
      }
      if (this.frameId != null) return;
      if (typeof this.video.requestVideoFrameCallback !== 'function') {
        this.checkFallbackFrame();
        return;
      }
      const epoch = this.epoch;
      this.frameId = this.video.requestVideoFrameCallback(() => {
        this.frameId = null;
        if (this.lifecycle && epoch === this.epoch && !this.failed && this.video.readyState >= 2) this.reveal();
      });
    }

    checkFallbackFrame() {
      if (!this.video || typeof this.video.requestVideoFrameCallback === 'function') return;
      if (!this.failed && !this.video.paused && this.video.readyState >= 2 && this.video.currentTime > 0) this.reveal();
    }

    reveal() {
      if (!this.hasAttribute('data-ready')) this.setAttribute('data-ready', '');
    }

    cancelFrame() {
      if (this.frameId != null && this.video?.cancelVideoFrameCallback) {
        this.video.cancelVideoFrameCallback(this.frameId);
      }
      this.frameId = null;
    }
  }

  customElements.define('ambient-video', AmbientVideo);
})();
